import React, { useState, useRef, useEffect } from "react";
import { CalendarIcon, ChevronDownIcon, Plus, X } from "lucide-react";

export const FormInput = ({
  label,
  type,
  value,
  onChange,
  onBlur,
  error,
  touched,
  required,
  placeholder,
  options,
  prefix,
  className = "",
  min,
  max,
  rows = 3,
  optional,
  note,
  addNew,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (value && options) {
      if (typeof value === "string") {
        const selectedOption = options.find(
          (option) => option?.value?.toLowerCase() === value?.toLowerCase()
        );
        setSelectedLabel(selectedOption ? selectedOption.label : "");
        setSearchTerm(selectedOption ? selectedOption.label : "");
      }
    } else {
      setSelectedLabel("");
      setSearchTerm("");
    }
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const inputClasses = `w-full px-3 py-2 bg-white border ${
    error && touched ? "border-red-500" : "border-gray-300"
  } rounded-md focus:outline-none focus:ring-1 focus:ring-offset-0 ${
    error && touched
      ? "focus:ring-red-300 focus:border-red-500"
      : "focus:ring-gray-300 focus:border-gray-300"
  } text-sm text-gray-900 placeholder:text-gray-500 transition-colors duration-200 ${className}`;

  const renderInput = () => {
    switch (type) {
      case "multiSelect":
        const isValue = Array.isArray(value) && value.length > 0;
        return (
          <div className="relative" ref={dropdownRef}>
            {/* Selected Pills */}
            <div className={`flex flex-wrap gap-2 ${isValue ? "mb-2" : ""}`}>
              {isValue &&
                value.map((selectedValue) => {
                  const selectedOption = options?.find(
                    (opt) => opt.value === selectedValue
                  );
                  // Handle both existing options and custom values
                  const displayLabel = selectedOption
                    ? selectedOption.label
                    : selectedValue;
                  const isCustomValue = !selectedOption;

                  return (
                    <div
                      key={selectedValue}
                      className={`flex items-center gap-1 px-2 py-1 rounded-full text-sm bg-blue-50 text-blue-900`}
                    >
                      <span>{displayLabel}</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          const newValue = value.filter(
                            (v) => v !== selectedValue
                          );
                          onChange(newValue);
                        }}
                        className={`hover:opacity-70 text-blue-700`}
                      >
                        <X size={14} strokeWidth={2.5} />
                      </button>
                    </div>
                  );
                })}
            </div>

            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  const newSearchTerm = e.target.value;
                  setSearchTerm(newSearchTerm);
                  setIsTyping(true);
                  if (!isOpen) {
                    setIsOpen(true);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && searchTerm.trim()) {
                    e.preventDefault();
                    // Check if the search term matches any existing option
                    const existingOption = options?.find(
                      (option) =>
                        option.label.toLowerCase() === searchTerm.toLowerCase()
                    );

                    if (!existingOption) {
                      // Add custom value if it doesn't exist
                      const newValue = Array.isArray(value) ? [...value] : [];
                      if (!newValue.includes(searchTerm)) {
                        newValue.push(searchTerm);
                        onChange(newValue);
                      }
                    } else {
                      // Add existing option if it matches exactly
                      const newValue = Array.isArray(value) ? [...value] : [];
                      if (!newValue.includes(existingOption.value)) {
                        newValue.push(existingOption.value);
                        onChange(newValue);
                      }
                    }
                    setSearchTerm("");
                    setIsOpen(false);
                    setIsTyping(false);
                  }
                }}
                onFocus={() => {
                  setIsOpen(true);
                  setIsTyping(false);
                }}
                onBlur={(e) => {
                  setTimeout(() => {
                    if (!isTyping) {
                      setIsOpen(false);
                      setSearchTerm("");
                    }
                  }, 200);
                  onBlur?.(e);
                }}
                className={`${inputClasses} text-left cursor-text hover:bg-gray-50 focus:bg-white ${
                  isOpen ? "ring-1 ring-gray-300 border-gray-300" : ""
                }`}
                placeholder={placeholder || "Search and select items"}
                required={required}
              />

              <ChevronDownIcon
                size={16}
                strokeWidth={2.5}
                className={`absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition-transform duration-200 ${
                  isOpen ? "rotate-180" : ""
                }`}
              />
            </div>

            {isOpen && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto hide-scrollbar">
                {/* Show count of filtered results */}
                {searchTerm && (
                  <div className="px-3 py-2 text-xs text-gray-500 border-b border-gray-100">
                    {options?.filter((option) =>
                      option.label
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())
                    ).length || 0}{" "}
                    result
                    {options?.filter((option) =>
                      option.label
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())
                    ).length !== 1
                      ? "s"
                      : ""}{" "}
                    found
                  </div>
                )}

                {/* Filter options based on search term */}
                {options
                  ?.filter((option) =>
                    option.label
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase())
                  )
                  .map((option) => {
                    const isSelected =
                      Array.isArray(value) && value.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault(); // Prevent blur event
                        }}
                        onClick={() => {
                          const newValue = Array.isArray(value)
                            ? [...value]
                            : [];
                          const valueIndex = newValue.indexOf(option.value);

                          if (valueIndex === -1) {
                            newValue.push(option.value);
                          } else {
                            newValue.splice(valueIndex, 1);
                          }

                          onChange(newValue);
                          setSearchTerm("");
                          setIsTyping(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none ${
                          isSelected
                            ? "bg-blue-50 text-blue-900"
                            : "text-gray-900"
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}

                {/* Show message if no options match */}
                {options?.filter((option) =>
                  option.label.toLowerCase().includes(searchTerm.toLowerCase())
                ).length === 0 &&
                  searchTerm && (
                    <div className="px-3 py-2 text-sm text-gray-500 text-center">
                      <div className="mb-2">
                        No options found for "{searchTerm}"
                      </div>
                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault(); // Prevent blur event
                        }}
                        onClick={() => {
                          // Add custom value
                          const newValue = Array.isArray(value)
                            ? [...value]
                            : [];
                          newValue.push(searchTerm);
                          onChange(newValue);
                          setSearchTerm("");
                          setIsOpen(false);
                          setIsTyping(false);
                        }}
                        className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 text-sm font-medium"
                      >
                        <Plus size={14} strokeWidth={2.5} />
                        Add "{searchTerm}"
                      </button>
                    </div>
                  )}

                {/* Show all options if no search term */}
                {!searchTerm && options?.length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-500 text-center">
                    No options available
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case "select":
        return (
          <div className="relative" ref={dropdownRef}>
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  const newSearchTerm = e.target.value;
                  setSearchTerm(newSearchTerm);
                  setIsTyping(true);

                  // If user clears the input, clear the selection
                  if (!newSearchTerm) {
                    console.log("Clearing selection");
                    onChange("");
                    setSelectedLabel("");
                  }

                  // Open dropdown when typing
                  if (!isOpen) {
                    setIsOpen(true);
                  }
                }}
                onFocus={() => {
                  setIsOpen(true);
                  setIsTyping(false);
                }}
                onBlur={(e) => {
                  // Delay closing to allow for option selection
                  setTimeout(() => {
                    if (!isTyping) {
                      setIsOpen(false);
                    }
                  }, 200);
                  onBlur?.(e);
                }}
                className={`${inputClasses} text-left cursor-text hover:bg-gray-50 focus:bg-white ${
                  isOpen ? "ring-1 ring-gray-300 border-gray-300" : ""
                }`}
                placeholder={placeholder}
                required={required}
              />

              {/* Clear button when option is selected */}
              {selectedLabel && (
                <button
                  type="button"
                  onClick={() => {
                    onChange("");
                    setSelectedLabel("");
                    setSearchTerm("");
                    setIsOpen(false);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                  <X size={16} strokeWidth={2.5} />
                </button>
              )}

              {/* Dropdown arrow */}
              {!selectedLabel && (
                <ChevronDownIcon
                  size={16}
                  strokeWidth={2.5}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition-transform duration-200 ${
                    isOpen ? "rotate-180" : ""
                  }`}
                />
              )}
            </div>

            {isOpen && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto hide-scrollbar">
                {/* Show count of filtered results */}
                {searchTerm && (
                  <div className="px-3 py-2 text-xs text-gray-500 border-b border-gray-100">
                    {options?.filter((option) =>
                      option.label
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())
                    ).length || 0}{" "}
                    result
                    {options?.filter((option) =>
                      option.label
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())
                    ).length !== 1
                      ? "s"
                      : ""}{" "}
                    found
                  </div>
                )}

                {/* Filter options based on search term */}
                {(() => {
                  const filteredOptions = options?.filter((option) =>
                    option.label
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase())
                  ) || [];

                  // Check if options have groups
                  const hasGroups = options?.[0]?.group;
                  
                  if (hasGroups) {
                    // Group options by their group property
                    const groupedOptions = filteredOptions.reduce((acc, option) => {
                      const group = option.group || 'Other';
                      if (!acc[group]) {
                        acc[group] = [];
                      }
                      acc[group].push(option);
                      return acc;
                    }, {});

                    return Object.entries(groupedOptions).map(([groupName, groupOptions]) => (
                      <div key={groupName}>
                        {/* Group Header */}
                        <div className="px-3 py-2 text-xs font-bold text-blue-700 uppercase tracking-wider bg-blue-50 border-b border-blue-200">
                          {groupName}
                        </div>
                        {/* Group Options */}
                        {groupOptions.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault(); // Prevent blur event
                            }}
                            onClick={() => {
                              console.log("Option clicked:", option);
                              onChange(option.value);
                              setSelectedLabel(option.label);
                              setSearchTerm(option.label);
                              setIsOpen(false);
                              setIsTyping(false);
                            }}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none ${
                              value === option.value
                                ? "bg-blue-50 text-blue-900"
                                : "text-gray-900"
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    ));
                  } else {
                    // Regular non-grouped options
                    return filteredOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault(); // Prevent blur event
                        }}
                        onClick={() => {
                          console.log("Option clicked:", option);
                          onChange(option.value);
                          setSelectedLabel(option.label);
                          setSearchTerm(option.label);
                          setIsOpen(false);
                          setIsTyping(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none ${
                          value === option.value
                            ? "bg-blue-50 text-blue-900"
                            : "text-gray-900"
                        }`}
                      >
                        {option.label}
                      </button>
                    ));
                  }
                })()}

                {/* Show message if no options match */}
                {options?.filter((option) =>
                  option.label.toLowerCase().includes(searchTerm.toLowerCase())
                ).length === 0 &&
                  searchTerm &&
                  (addNew ? (
                    <button
                      onMouseDown={(e) => {
                        e.preventDefault(); // Prevent blur event
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        addNew(label);
                      }}
                      className="px-3 py-2 text-sm text-gray-500 text-center flex items-center gap-2"
                    >
                      <Plus size={16} strokeWidth={2.5} />
                      Add New {label}
                    </button>
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-500 text-center">
                      No options found for "{searchTerm}"
                    </div>
                  ))}

                {/* Show all options if no search term */}
                {!searchTerm && options?.length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-500 text-center">
                    No options available
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case "checkbox":
        return (
          <div className="form-checkbox-wrapper">
            <input
              type="checkbox"
              id={label}
              checked={!!value}
              onChange={(e) => onChange(e.target.checked)}
              onBlur={onBlur}
              className="form-checkbox"
              required={required}
            />
            <label htmlFor={label} className="form-checkbox-label">
              {placeholder || label}
            </label>
          </div>
        );

      case "textarea":
        return (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            className={`${inputClasses} min-h-[120px] resize-none`}
            placeholder={placeholder}
            required={required}
            rows={rows}
          />
        );

      case "date":
        return (
          <div className="relative">
            <input
              type="date"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onBlur={onBlur}
              className={`${inputClasses} ${value ? "" : "text-[#6C757D]"}`}
              required={required}
              placeholder={placeholder || "yyyy/mm/dd"}
            />
          </div>
        );

      case "number":
        return (
          <div className="relative">
            {prefix && (
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6C757D] text-sm">
                {prefix}
              </span>
            )}
            <input
              type="number"
              value={value}
              onChange={(e) => {
                const val = e.target.value;
                // Allow empty string or valid numbers
                if (val === "" || !isNaN(Number(val))) {
                  onChange(val);
                }
              }}
              onBlur={onBlur}
              className={`${inputClasses} ${prefix ? "pl-7" : ""}`}
              placeholder={placeholder}
              required={required}
              min={min}
              max={max}
              step={type === "number" ? "1" : undefined} // Allow decimals for numbers
            />
          </div>
        );

      case "input":
        // If options are provided, show as recommendations in dropdown
        if (options && Array.isArray(options) && options.length > 0) {
          return (
            <div className="relative" ref={dropdownRef}>
              <input
                type="text"
                value={value}
                onChange={(e) => {
                  const newSearchTerm = e.target.value;
                  setSearchTerm(newSearchTerm);
                  setIsTyping(true);

                  onChange(newSearchTerm);

                  // If user clears the input, clear the selection
                  if (!newSearchTerm) {
                    setSelectedLabel("");
                    setIsOpen(false);
                  } else {
                    setIsOpen(true);
                  }
                }}
                onFocus={() => {
                  setIsOpen(true);
                  setIsTyping(false);
                }}
                onBlur={(e) => {
                  // Delay closing to allow for option selection
                  setTimeout(() => {
                    if (!isTyping) {
                      setIsOpen(false);
                    }
                  }, 200);
                  onBlur?.(e);
                }}
                className={`${inputClasses} text-left cursor-text hover:bg-gray-50 focus:bg-white ${
                  isOpen ? "ring-1 ring-gray-300 border-gray-300" : ""
                }`}
                placeholder={placeholder}
                required={required}
              />
              {/* Dropdown for recommendations */}
              {isOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded shadow-lg max-h-48 overflow-auto">
                  {options
                    .filter((option) =>
                      option.label.toLowerCase().includes(value?.toLowerCase())
                    )
                    .map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                        onMouseDown={(e) => {
                          e.preventDefault(); // Prevent blur event
                        }}
                        onClick={() => {
                          // When clicking recommendation, populate the input with the label
                          setSearchTerm(option.label);
                          setSelectedLabel(option.label);
                          // Call onChange with the label (user-friendly text)
                          onChange(option.label);
                          setIsOpen(false);
                          setIsTyping(false);
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                </div>
              )}
            </div>
          );
        } else {
          // Fallback to normal input if no options
          return (
            <input
              type="text"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onBlur={onBlur}
              className={inputClasses}
              placeholder={placeholder}
              required={required}
              rows={rows}
            />
          );
        }
      default:
        return (
          <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            className={inputClasses}
            placeholder={placeholder}
            required={required}
          />
        );
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-700">
        <span className="text-gray-600 font-bold">{label}</span>
        {required && <span className="text-red-500 ml-1">*</span>}
        {optional && (
          <span className="text-gray-500 ml-1 text-xs">(Optional)</span>
        )}
      </label>
      <div className="space-y-1">
        {renderInput()}
        {note && <p className="text-yellow-500 text-xs">note : {note}</p>}
        {error && touched && (
          <p className="text-red-500 text-xs font-medium">{error}</p>
        )}
      </div>
    </div>
  );
};
